/**
 * App Root Component
 *
 * Sets up routing and query client for the application.
 * Routes:
 * - /signin, /signup: Authentication page
 * - /: Protected home dashboard (redirects to signin if not authenticated)
 * - /boards/:boardId: Protected board page
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from './hooks/useAuth';
import { AuthPage } from './pages/Auth';
import { Home } from './pages/Home';
import { BoardPage } from './pages/BoardPage';
import { VerifyEmailPage } from './pages/VerifyEmail';
import { ResetPasswordPage } from './pages/ResetPassword';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { KnowledgeSpacesPage } from './pages/knowledge/KnowledgeSpacesPage';
import { KnowledgeSpaceDetailPage } from './pages/knowledge/KnowledgeSpaceDetailPage';
import './App.css';

// Create query client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * Protected route wrapper.
 * Redirects to /signin if not authenticated.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
}

/**
 * App routes configuration.
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Auth routes */}
      <Route path="/signin" element={<AuthPage />} />
      <Route path="/signup" element={<AuthPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected home dashboard route */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />

      {/* Board route */}
      <Route
        path="/boards/:boardId"
        element={
          <ProtectedRoute>
            <BoardPage />
          </ProtectedRoute>
        }
      />

      {/* Knowledge spaces */}
      <Route
        path="/knowledge"
        element={
          <ProtectedRoute>
            <KnowledgeSpacesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge/:spaceId"
        element={
          <ProtectedRoute>
            <KnowledgeSpaceDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Main App component.
 * Wraps application with providers: QueryClient, BrowserRouter.
 */
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
